import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { jsPDF } from 'jspdf';
import AssessmentForm from './components/AssessmentForm';
import RadarChart from './components/RadarChart';
import UserDetailsForm from './components/UserDetailsForm';
import { ArrowRight, RefreshCw, Send } from 'lucide-react';

function App() {
  // Views: intro, assessment, details, results
  const [view, setView] = useState('intro');
  const [scores, setScores] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [emailStatus, setEmailStatus] = useState('idle');
  const [hasSentEmail, setHasSentEmail] = useState(false);

  // We need a reference to the chart to capture its image for the PDF
  const chartRef = useRef(null);

  // Initialize EmailJS
  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'Ct_cpuR2CsvjqTugn';
    console.log('Initializing EmailJS with Public Key:', publicKey ? 'Present' : 'Missing');
    emailjs.init(publicKey);
  }, []);

  // Load state from localStorage on mount
  useEffect(() => {
    // 1. Check URL Params (Priority: Sharing/Email Link)
    const searchParams = new URLSearchParams(window.location.search);
    const sharedScores = searchParams.get('scores');
    const sharedName = searchParams.get('name');

    if (sharedScores) {
      try {
        // Fix: Replace spaces with '+' because URL decoding might have stripped them
        const fixedScores = sharedScores.replace(/ /g, '+');
        const fixedName = sharedName ? sharedName.replace(/ /g, '+') : '';

        // Decode: Base64 -> JSON
        const decodedScores = JSON.parse(atob(fixedScores));
        setScores(decodedScores);

        if (fixedName) {
          setUserDetails({ name: atob(fixedName), email: '' });
        }

        // Prevent re-sending email when explicitly viewing a link
        setHasSentEmail(true);
        setView('results');
        return; // Skip localStorage if URL params exist
      } catch (e) {
        console.error("Failed to parse URL params", e);
      }
    }

    // 2. Fallback to LocalStorage
    const savedData = localStorage.getItem('wheelOfLifeData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.scores) setScores(parsed.scores);
        if (parsed.userDetails) setUserDetails(parsed.userDetails);

        // Restore view state based on data presence
        if (parsed.scores && parsed.userDetails) {
          setView('results');
        } else if (parsed.scores) {
          setView('details');
        }
      } catch (e) {
        console.error("Failed to parse local storage", e);
      }
    }
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (scores || userDetails) {
      localStorage.setItem('wheelOfLifeData', JSON.stringify({ scores, userDetails }));
    }
  }, [scores, userDetails]);

  // TRIGGER EMAIL AND PDF GENERATION
  // This effect runs only when we are in 'results' view, have user details, and haven't sent yet.
  // We wait a brief moment to ensure the Chart canvas is rendered.
  useEffect(() => {
    const hasValidEmail = userDetails?.email && userDetails.email.trim() !== '';

    if (view === 'results' && userDetails && scores && !hasSentEmail && emailStatus === 'idle' && hasValidEmail) {

      const generateAndSend = async () => {
        setEmailStatus('sending');
        await new Promise(r => setTimeout(r, 1000));

        try {
          const doc = new jsPDF();

          doc.setFillColor(245, 242, 238);
          doc.rect(0, 0, 210, 297, 'F');

          const drawCard = (x, y, w, h) => {
            doc.setFillColor(225, 220, 214);
            doc.roundedRect(x + 1, y + 1, w, h, 6, 6, 'F');
            doc.setFillColor(255, 255, 255);
            doc.roundedRect(x, y, w, h, 6, 6, 'F');
          };

          try {
            const logoImg = await new Promise((resolve) => {
              const img = new Image();
              img.src = '/logo.png';
              img.onload = () => resolve(img);
              img.onerror = () => resolve(null);
            });
            if (logoImg) {
              doc.addImage(logoImg, 'PNG', 95, 10, 20, 20);
            }
          } catch (e) {
            console.error("Logo addition failed", e);
          }

          doc.setFontSize(24);
          doc.setTextColor(68, 64, 60);
          doc.text("Wheel of Life", 105, 35, { align: "center" });

          doc.setFontSize(11);
          doc.setTextColor(120, 113, 108);
          doc.text(
            `Prepared for ${userDetails.name} • ${new Date().toLocaleDateString()}`,
            105,
            42,
            { align: "center" }
          );

          drawCard(15, 50, 85, 140);

          doc.setFontSize(14);
          doc.setTextColor(68, 64, 60);
          doc.text("Your Life Balance", 57.5, 65, { align: "center" });

          if (chartRef.current) {
            try {
              const chartImage = chartRef.current.toBase64Image('image/png', 1.0);
              if (chartImage && chartImage !== 'undefined') {
                doc.addImage(chartImage, 'PNG', 25, 78, 65, 65);
              } else {
                if (chartRef.current.canvas) {
                  const canvasImg = chartRef.current.canvas.toDataURL("image/png");
                  doc.addImage(canvasImg, 'PNG', 25, 78, 65, 65);
                }
              }
            } catch (err) {
              console.error("Error capturing chart for PDF:", err);
            }
          }

          drawCard(110, 50, 85, 140);

          doc.setFontSize(14);
          doc.setTextColor(68, 64, 60);
          doc.text("Category Breakdown", 152.5, 65, { align: "center" });

          let y = 80;

          Object.entries(scores).forEach(([category, score]) => {
            doc.setFontSize(10);
            doc.setTextColor(68, 64, 60);
            doc.text(category, 115, y);

            doc.setTextColor(120, 113, 108);
            doc.text(`${score}/20`, 190, y, { align: "right" });

            y += 4;

            doc.setFillColor(245, 245, 244);
            doc.roundedRect(115, y, 70, 3, 1.5, 1.5, 'F');

            if (score >= 16) doc.setFillColor(102, 82, 73);     
            else if (score >= 10) doc.setFillColor(159, 140, 129);
            else doc.setFillColor(214, 211, 209);                
            
            const barW = (score / 20) * 70;
            if (barW > 0) {
              doc.roundedRect(115, y, barW, 3, 1.5, 1.5, 'F');
            }

            y += 8;

            doc.setFontSize(8);
            doc.setTextColor(168, 162, 158);
            doc.text(
              score >= 16 ? "Thriving" : score >= 10 ? "Balanced" : "Needs Attention",
              115,
              y
            );

            y += 10;
          });

          doc.setFontSize(9);
          doc.setTextColor(168, 162, 158);
          doc.text("Generated by SeleneAxiora", 105, 210, { align: "center" });

          const totalScore = Object.values(scores).reduce((a, b) => a + b, 0);

          const scoresParam = encodeURIComponent(btoa(JSON.stringify(scores)));
          const nameParam = encodeURIComponent(btoa(userDetails.name));
          const resultLink = `${window.location.origin}?scores=${scoresParam}&name=${nameParam}`;

          const templateParams = {
            to_name: userDetails.name,    
            user_name: userDetails.name,  
            to_email: userDetails.email,  
            email: userDetails.email,   
            user_email: userDetails.email, 
            recipient_email: userDetails.email, 
            reply_to: userDetails.email,
            career: scores['Career'],
            health: scores['Health'],
            finance: scores['Finance'],
            relationships: scores['Relationships'],
            growth: scores['Growth'],

            career_score: scores['Career'],
            health_score: scores['Health'],
            finance_score: scores['Finance'],
            relationships_score: scores['Relationships'],
            growth_score: scores['Growth'],

            Career: scores['Career'],
            Health: scores['Health'],
            Finance: scores['Finance'],
            Relationships: scores['Relationships'],
            Growth: scores['Growth'],

            total: `${totalScore}/100`,
            total_score: `${totalScore}/100`,

            result_link: resultLink,

            message: "Your PDF report has been generated successfully on screen. Please save it from there if needed."
          };

          doc.save("Wheel_of_Life_Report.pdf");
          
          console.log('Sending email to:', userDetails.email);
          console.log('Template params:', templateParams);

          const response = await emailjs.send(
            process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_sc1v0kz',
            process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_dqd49qc',
            templateParams
          );

          console.log('EmailJS Response:', response.status, response.text);
          setHasSentEmail(true);
          setEmailStatus('success');
        } catch (error) {
          console.error("Process failed:", error);
          console.error("Error details:", error.text || error.message);
          setEmailStatus('error');
        }
      };

      generateAndSend();
    }
  }, [view, userDetails, scores, hasSentEmail, emailStatus]);

  const startAssessment = () => {
    setView('assessment');
  };

  const handleAssessmentComplete = (finalScores) => {
    setScores(finalScores);
    setView('details');
    
  };

  const handleDetailsSubmit = (details) => {
    setUserDetails(details);
    setView('results');
  };

  const retakeAssessment = () => {
    setScores(null);
    setUserDetails(null);
    setHasSentEmail(false);
    setEmailStatus('idle');
    localStorage.removeItem('wheelOfLifeData');
    setView('intro');
  };

  return (
    <div className="min-h-screen bg-sand-100 text-earth-900 py-12 px-4 selection:bg-earth-100 selection:text-earth-800">

      {/* Header */}
      <header className="max-w-4xl mx-auto mb-12 text-center flex flex-col items-center">
        <img src="/logo.png" alt="Logo" className="w-24 h-24 object-contain mb-4" />
        <h1 className="text-4xl md:text-5xl font-display font-bold text-earth-900 tracking-tight mb-4">
          Wheel of <span className="text-earth-500">Life</span>
        </h1>
        <p className="text-lg text-stone-600 max-w-xl mx-auto">
          Gain clarity on your life's balance. Assess where you are today to design where you want to be tomorrow.
        </p>
      </header>

      <main className="max-w-4xl mx-auto">
        {view === 'intro' && (
          <div className="card-premium max-w-xl mx-auto text-center space-y-8 animate-fade-in">
            <div className="w-24 h-24 mx-auto mb-6 transform hover:scale-110 transition-transform duration-300">
              <img src="/logo.png" alt="Wheel of Life Logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-4">Ready to discover your balance?</h2>
              <p className="text-stone-500 leading-relaxed mb-6">
                This assessment consists of 25 questions across 5 key areas of life:
                Career, Health, Finance, Relationships, and Personal Growth.
              </p>
              <button
                onClick={startAssessment}
                className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
              >
                Start Assessment <ArrowRight size={20} />
              </button>
            </div>
          </div>
        )}

        {view === 'assessment' && (
          <AssessmentForm onComplete={handleAssessmentComplete} />
        )}

        {view === 'details' && (
          <div className="max-w-xl mx-auto animate-slide-up">
            <div className="mb-6 text-center">
              <h2 className="text-2xl font-bold text-earth-900 mb-2">Assessment Complete!</h2>
              <p className="text-stone-500">Enter your details to reveal your Wheel of Life and receive your personal report.</p>
            </div>
            <UserDetailsForm onSubmit={handleDetailsSubmit} />
          </div>
        )}

        {view === 'results' && scores && (
          <div className="animate-fade-in space-y-8">
            <div className="grid md:grid-cols-2 gap-8 items-start">
              {/* Chart Section */}
              <div className="card-premium flex flex-col items-center justify-center min-h-[450px]">
                <h3 className="text-xl font-bold mb-6 text-earth-800">Your Life Balance</h3>

                {/* PASSING REF TO CHART TO CAPTURE IMAGE */}
                <RadarChart ref={chartRef} categoryScores={scores} />

                {emailStatus === 'sending' && (
                  <div className="mt-4 text-sm text-earth-600 flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-earth-600 border-t-transparent rounded-full" />
                    Generating PDF & Sending report to {userDetails?.email}...
                  </div>
                )}
                {emailStatus === 'success' && (
                  <div className="mt-4 text-sm text-green-600 flex items-center gap-2">
                    <Send size={16} /> Report sent & Downloaded!
                  </div>
                )}
                {emailStatus === 'error' && (
                  <div className="mt-4 text-sm text-red-500">
                    Couldn't send email. The PDF has been downloaded locally.
                  </div>
                )}
              </div>

              {/* Analysis Section */}
              <div className="space-y-6">
                <div className="card-premium">
                  <h3 className="text-xl font-bold mb-6 text-earth-800">Category Breakdown</h3>
                  <div className="space-y-4">
                    {Object.entries(scores).map(([category, score]) => (
                      <div key={category}>
                        <div className="flex justify-between items-end mb-1">
                          <span className="font-semibold text-stone-700">{category}</span>
                          <span className="text-sm font-medium text-stone-500">{score}/20</span>
                        </div>
                        <div className="h-2 w-full bg-stone-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${score >= 16 ? 'bg-earth-600' : // High
                              score >= 10 ? 'bg-earth-400' : // Medium
                                'bg-stone-300' // Low
                              }`}
                            style={{ width: `${(score / 20) * 100}%` }}
                          />
                        </div>
                        <p className="text-xs text-stone-400 mt-1">
                          {score >= 16 ? "Thriving" : score >= 10 ? "Balanced" : "Needs Attention"}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={retakeAssessment}
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-xl border border-stone-200 text-stone-600 hover:bg-white hover:text-earth-600 font-medium transition-all"
                >
                  <RefreshCw size={18} />
                  Start Over
                </button>
              </div>
            </div>

            <div className="card-premium text-center bg-earth-800 text-white">
              <h3 className="text-2xl text-white font-bold mb-4">What's Next?</h3>
              <p className="text-earth-100 max-w-2xl mx-auto mb-8">
                Reflect on the areas where you scored lower. Set one small, actionable goal for each of those categories to improve your wheel's balance over the next 30 days.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
